import React from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import StageCard from '../ui/StageCard';
import { 
  StyleSpecificScoringCardSkeleton, 
  PerformanceScheduleSkeleton, 
  TotalScoreCardSkeleton 
} from '../ui/SkeletonLoader.js';
import { LayoutConfig } from '../../types/judge';

interface SimpleScoringLayoutSkeletonProps {
  layout: LayoutConfig;
  showStyleCard?: boolean; // Whether to show the style-specific scoring card skeleton
}

/**
 * Skeleton loader that matches the SimpleScoringLayout structure
 * Shows appropriate skeletons based on expected layout configuration
 */
const SimpleScoringLayoutSkeleton: React.FC<SimpleScoringLayoutSkeletonProps> = ({
  layout,
  showStyleCard = true // Default to showing it during loading since we don't know yet
}) => {
  return (
    <>
      {/* Loading indicator overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute top-4 right-6 z-50 flex items-center gap-2 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/10"
      >
        <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
        <span className="text-sm text-white/80">Loading judge interface...</span>
      </motion.div>
      {/* First column - StyleSpecificScoringCard Skeleton (2/3 width) - Only show if expected */}
      {showStyleCard && (
        <div className="col-span-2 min-h-0 flex flex-col overflow-hidden pl-6 pr-1">
          <StageCard className={`flex-grow min-h-0 overflow-hidden ${layout.cardPadding}`} glowColor="purple">
            <StyleSpecificScoringCardSkeleton className="h-full" />
          </StageCard>
        </div>
      )}

      {/* Second column - Schedule + Total stacked */}
      <div className={`${showStyleCard ? 'col-span-1 pl-1' : 'col-span-3 pl-6'} min-h-0 flex flex-col overflow-hidden pr-6 gap-4`}>
        {/* Expanded PerformanceSchedule takes most of the space */}
        <div className="flex-grow min-h-0 overflow-hidden rounded-2xl">
          <StageCard className={`h-full overflow-hidden ${layout.cardPadding}`} glowColor="orange">
            <PerformanceScheduleSkeleton className="h-full" />
          </StageCard>
        </div>

        {/* TotalScoreCard positioned at bottom */}
        <div className="flex-shrink-0 overflow-auto" style={{ maxHeight: '30vh' }}>
          <StageCard className={layout.cardPadding} glowColor="purple">
            <TotalScoreCardSkeleton className="h-full" />
          </StageCard>
        </div>
      </div>
    </>
  );
};

export default SimpleScoringLayoutSkeleton;
